// THE grocery list and THE pantry (one each per user). Rules live in logic/grocery.ts.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid/non-secure';
import { persistStorage } from './storage';
import { addRecipes, addToPantry, moveCheckedToPantry, setChecked } from '../logic/grocery';
import type { Amount } from '../logic/quantity';
import type { CustomUnit, UnitRef } from '../logic/units';
import type { Day, Grocery, Id, Pantry, Recipe } from '../types';

type State = { grocery: Grocery; pantry: Pantry };
type Actions = {
  addRecipes: (recipes: Recipe[], exclude?: Set<Id>) => number;
  addLooseItem: (kind: 'ingredient' | 'utensil', refId: Id, amount: Amount, unit: UnitRef) => void;
  setChecked: (itemIds: Id[], checked: boolean) => void;
  setCardServings: (cardId: Id, servings: number) => void;
  removeFromGrocery: (cardIds: Id[], itemIds: Id[]) => void;
  moveToPantry: (recipeServings: Record<Id, number>, units: Record<string, CustomUnit>, today: Day) => void;
  removeFromPantry: (keys: string[]) => void;
  setPantryDirty: (refId: Id, dirty: boolean) => void;
  addToPantry: (items: { refId: Id; amount: Amount; unit: UnitRef }[], units: Record<string, CustomUnit>, today: Day) => void;
};

export const pantryKey = (p: { kind: string; refId: string; unit: UnitRef }) => `${p.kind}:${p.refId}:${JSON.stringify(p.unit)}`;

export const useShopping = create<State & Actions>()(
  persist(
    (set, get) => ({
      grocery: { recipes: [], looseItems: [], updatedAt: 0 },
      pantry: { items: [], updatedAt: 0 },
      addRecipes: (recipes, exclude) => {
        const res = addRecipes(get().grocery, recipes, exclude);
        set({ grocery: res.grocery });
        return res.added;
      },
      addLooseItem: (kind, refId, amount, unit) =>
        set(s => ({
          grocery: { ...s.grocery, looseItems: [...s.grocery.looseItems, { id: nanoid(), kind, refId, amount, unit, checked: false }], updatedAt: Date.now() },
        })),
      setChecked: (ids, checked) => set(s => ({ grocery: setChecked(s.grocery, ids, checked) })),
      setCardServings: (cardId, servings) =>
        set(s => ({
          grocery: { ...s.grocery, recipes: s.grocery.recipes.map(c => (c.id === cardId ? { ...c, servings: Math.min(99, Math.max(1, servings)) } : c)), updatedAt: Date.now() },
        })),
      removeFromGrocery: (cardIds, itemIds) =>
        set(s => ({
          grocery: {
            recipes: s.grocery.recipes.filter(c => !cardIds.includes(c.id)),
            looseItems: s.grocery.looseItems.filter(i => !itemIds.includes(i.id)),
            updatedAt: Date.now(),
          },
        })),
      moveToPantry: (servings, units, today) => set(s => moveCheckedToPantry(s.grocery, s.pantry, servings, units, today)),
      removeFromPantry: keys => set(s => ({ pantry: { items: s.pantry.items.filter(p => !keys.includes(pantryKey(p))), updatedAt: Date.now() } })),
      addToPantry: (items, units, today) => set(s => ({ pantry: addToPantry(s.pantry, items, units, today) })),
      setPantryDirty: (refId, dirty) =>
        set(s => ({ pantry: { items: s.pantry.items.map(p => (p.kind === 'utensil' && p.refId === refId ? { ...p, dirty } : p)), updatedAt: Date.now() } })),
    }),
    { name: 'shopping', storage: persistStorage, version: 1, partialize: s => ({ grocery: s.grocery, pantry: s.pantry }) },
  ),
);
